import React, { useContext, useEffect, useState } from "react";
import { useDistributorsContext } from "./distributorsContext";
import { useInternalSkuContext } from "./internalSkuContext";
import { isInt } from "../Utilities/utilities.js";
import { useProductsContext } from "./productsContext.jsx";
import { MAX_DISTRIBUTORS_IN_URL } from "../Utilities/constants.ts";
import PropTypes from 'prop-types';

const url = `/forecastreview`;

export const NewItemForecastContext = React.createContext(null);

export function NewItemForecastContextProvider(props) {
    const { selectedDistributors } = useDistributorsContext();
    const { selectedProducts } = useProductsContext();
    const { selectedInternalSkus } = useInternalSkuContext();
    const [originalForecastReviewItems, setOriginalForecastReviewItems] = useState([]);
    const [forecastReviewItems, setForecastReviewItems] = useState([]);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState("");

    const get = async () => {
        if (selectedDistributors && selectedDistributors.length > 0) {
            const controller = new AbortController();
            const signal = controller.signal;
            const useBody = selectedDistributors.length > MAX_DISTRIBUTORS_IN_URL;

            try {
                const response = useBody
                    ? await fetch(`${url}/newitems`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ distributorIds: selectedDistributors.map(d => d.distributorId) }),
                        signal
                    })
                    : await fetch(`${url}/newitems?${selectedDistributors.map(d => `distributorIds=${d.distributorId}`).join("&")}`, { signal });

                const data = await response.json();
                if (response.ok) {
                    const selectedSKUs = selectedProducts?.map(selectedProduct => selectedProduct.sku);
                    setOriginalForecastReviewItems(data.forecastReviewItems);
                    let items = data.forecastReviewItems?.map((forecast, index) => {
                        let temp = structuredClone(forecast);
                        return { ...temp, id: index };
                    }) ?? [];
                    if (selectedSKUs?.length > 0) {
                        items = items.filter(forecastReviewItem => selectedSKUs.includes(forecastReviewItem.product.sku));
                    }
                    if (selectedInternalSkus?.length > 0) {
                        items = items.filter((forecastReviewItem) =>
                            selectedInternalSkus.some(
                                (s) =>
                                    s.internalSku === forecastReviewItem.customerPartNumber &&
                                    s.sapId === forecastReviewItem.distributorSapId &&
                                    s.sku === forecastReviewItem.product?.sku
                            )
                        );
                    }
                    setForecastReviewItems(items);
                    setIsReadOnly(data.isReadOnly);
                    setIsReady(!error && data.forecastReviewItems?.length >= 0);
                } else {
                    setError(data);
                }
            } catch (error) {
                console.error('Error:', error);
                setError(error);
            }
        } else {
            setOriginalForecastReviewItems([]);
            setIsReady(false);
            setError("");
        }
    };

    useEffect(() => {
        if (isReady) {
            const selectedSKUs = selectedProducts?.map(selectedProduct => selectedProduct.sku);
            const filterByProduct = selectedSKUs && selectedSKUs.length > 0;
            const filterByInternalSku = selectedInternalSkus && selectedInternalSkus.length > 0;

            let items = mapForecastItems(originalForecastReviewItems);
            if (filterByProduct) {
                items = items.filter(forecastReviewItem => selectedSKUs.includes(forecastReviewItem.product.sku));
            }
            if (filterByInternalSku) {
                items = items.filter((forecastReviewItem) =>
                    selectedInternalSkus.some(
                        (s) =>
                            s.internalSku === forecastReviewItem.customerPartNumber &&
                            s.sapId === forecastReviewItem.distributorSapId &&
                            s.sku === forecastReviewItem.product?.sku
                    )
                );
            }
            setForecastReviewItems(items);
        }
    }, [isReady, selectedProducts, selectedInternalSkus]);

    const getFormattedOriginalForecastReviewItems = (items) => {
        if (items) {
            return items.map((forecast, index) => {
                let temp = structuredClone(forecast);
                return { ...temp, id: index };
            })
        } else {
            return originalForecastReviewItems.map((forecast, index) => {
                let temp = structuredClone(forecast);
                return { ...temp, id: index };
            });
        }
    }

    const mapForecastItems = (forecastItems) => forecastItems.map((forecast, index) => {
        let temp = structuredClone(forecast);
        return { ...temp, id: index };
    });

    async function SaveForecastAdjustmentChanges() {
        let forecastAdjustmentsToUpdateOrCreate = [];
        let forecastAdjustmentsToDelete = [];
        let updatedForecastReviewItems = structuredClone(originalForecastReviewItems);

        forecastReviewItems.forEach((forecastReviewItem, index) => {
            forecastReviewItem.forecastAmounts?.forEach((forecastAmount) => {
                const originalForecastAmount = originalForecastReviewItems[index]?.forecastAmounts?.find(
                    original => original.weekId === forecastAmount.weekId
                );

                if (forecastAmount.adjustedAmount !== originalForecastAmount?.adjustedAmount) {
                    if (isInt(forecastAmount.adjustedAmount)) {
                        forecastAdjustmentsToUpdateOrCreate.push({
                            forecastAdjustmentId: forecastAmount.forecastAdjustmentId,
                            amount: forecastAmount.adjustedAmount,
                            sku: forecastReviewItem.product.sku,
                            distributorId: forecastReviewItem.distributorId,
                            weekId: forecastAmount.weekId,
                        });

                        if (originalForecastAmount) {
                            originalForecastAmount.adjustedAmount = forecastAmount.adjustedAmount;
                        } else {
                            if (!updatedForecastReviewItems[index].forecastAmounts) {
                                updatedForecastReviewItems[index].forecastAmounts = [];
                            }
                            updatedForecastReviewItems[index].forecastAmounts.push({
                                ...forecastAmount,
                                adjustedAmount: forecastAmount.adjustedAmount
                            });
                        }
                    } else if (forecastAmount.adjustedAmount === null || forecastAmount.adjustedAmount === undefined || forecastAmount.adjustedAmount === '') {
                        if (originalForecastAmount) {
                            originalForecastAmount.adjustedAmount = null;
                        } else {
                            if (!updatedForecastReviewItems[index].forecastAmounts) {
                                updatedForecastReviewItems[index].forecastAmounts = [];
                            }
                            updatedForecastReviewItems[index].forecastAmounts.push({
                                ...forecastAmount,
                                adjustedAmount: null
                            });
                        }

                        if (forecastAmount.forecastAdjustmentId) {
                            forecastAdjustmentsToDelete.push({ ...forecastAmount, distributorId: forecastReviewItem.distributorId });
                        }
                    }
                }
            });
        });

        if (forecastAdjustmentsToUpdateOrCreate.length > 0) {
            await PutForecastAdjustments(forecastAdjustmentsToUpdateOrCreate);
        }
        if (forecastAdjustmentsToDelete.length > 0) {
            await DeleteForecastAdjustments(forecastAdjustmentsToDelete);
        }

        setOriginalForecastReviewItems(updatedForecastReviewItems);

        if (forecastAdjustmentsToUpdateOrCreate.length > 0 || forecastAdjustmentsToDelete.length > 0) {
            await get();
        }
    }

    async function PutForecastAdjustments(forecastAdjustments) {
        const controller = new AbortController();
        const signal = controller.signal;

        for (const selectedDistributor of selectedDistributors) {
            const forecastDtos = forecastAdjustments.filter(forecast => forecast.distributorId === selectedDistributor.distributorId).map(adjustment => {
                return { weekId: adjustment.weekId, sku: adjustment.sku, amount: adjustment.amount.toString() };
            });

            if (forecastDtos?.length > 0) {
                const requestOptions = {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(forecastDtos),
                    signal: signal,
                };

                try {
                    const response = await fetch(`/api/Distributor/${selectedDistributor.sapId}/forecasts`, requestOptions);

                    if (!response.ok) {
                        const data = await response.json();
                        setError(data)
                    }
                } catch (error) {
                    console.error('Error:', error);
                    setError(error);
                }
            }
        }
    }

    async function DeleteForecastAdjustments(forecastAdjustments) {
        const controller = new AbortController();
        const signal = controller.signal;
        if (selectedDistributors) {
            for (const selectedDistributor of selectedDistributors) {
                const requestOptions = {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(forecastAdjustments.filter(forecastAdjustment => forecastAdjustment?.distributorId === selectedDistributor.distributorId).map(forecastAdjustment => forecastAdjustment.forecastAdjustmentId)),
                    signal: signal,
                };
                await fetch(`${url}/`, requestOptions)
                    .then(async (response) => {
                        const isJson = response.headers
                            .get("content-type")
                            ?.includes("application/json");
                        const data = isJson && (await response.json());

                        if (!response.ok) {
                            const error = (data && data.message) || response.status;
                            return Promise.reject(error);
                        }
                    })
                    .catch((error) => {
                        console.error('Error:', error);
                        setError(error);
                    });
            }
        }
    }

    return (<NewItemForecastContext.Provider
        value={{
            setForecastReviewItems,
            forecastReviewItems,
            SaveForecastAdjustmentChanges,
            isReady,
            isReadOnly,
            error,
            getFormattedOriginalForecastReviewItems,
            get,
        }}
    >
        {props.children}
    </NewItemForecastContext.Provider>);
}

NewItemForecastContextProvider.propTypes = {
    children: PropTypes.node
}

export function useNewItemForecastContext() {
    const context = useContext(NewItemForecastContext);

    if (!context) throw new Error("useNewItemForecastContext must be used within a NewItemForecastContextProvider.");
    return context;
}
